import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { ARTICLES } from "@/lib/articles";
import { usePageTitle } from "@/lib/site";

export default function Insights() {
  usePageTitle("Insights — Plain-Language Guides for Small Businesses & Tribal Governments | CalCor Solutions");

  return (
    <>
      <section data-testid="insights-hero" className="pt-36 pb-16 sm:pt-44 lg:pb-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <MaskedLine delay={0.15} className="text-xs font-semibold uppercase tracking-[0.25em] text-pine">
            Insights
          </MaskedLine>
          <h1 className="mt-6 font-serif text-5xl font-medium leading-[1.05] tracking-tighter text-ink sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.3}>Plain language,</MaskedLine>
            <MaskedLine delay={0.45}>
              <em className="text-pine">from the ledger.</em>
            </MaskedLine>
          </h1>
          <FadeUp delay={0.6}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-600 sm:text-lg">
              Short, honest guides on bookkeeping, accounting, and audit preparation — written
              for the small businesses and tribal governments we serve, in Seattle and beyond.
            </p>
          </FadeUp>
        </div>
      </section>

      <section data-testid="insights-list" className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          {ARTICLES.map((a, i) => (
            <FadeUp key={a.slug} delay={i * 0.08}>
              <Link
                to={`/insights/${a.slug}`}
                data-testid={`article-link-${a.slug}`}
                className="group grid gap-6 border-t border-black/10 py-12 transition-colors last:border-b hover:bg-white/60 sm:grid-cols-12 sm:items-center"
              >
                <span className="font-serif text-5xl italic leading-none text-pine/25 transition-colors duration-300 group-hover:text-pine sm:col-span-2">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="sm:col-span-9">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-clay">
                    {a.category} · {a.date} · {a.readTime}
                  </p>
                  <h2 className="mt-3 font-serif text-2xl leading-snug tracking-tight text-ink transition-colors duration-300 group-hover:text-pine sm:text-3xl">
                    {a.title}
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-600 sm:text-base">
                    {a.excerpt}
                  </p>
                </div>
                <span className="hidden justify-self-end sm:col-span-1 sm:inline-flex">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-black/10 transition-colors duration-300 group-hover:border-pine group-hover:bg-pine">
                    <ArrowUpRight className="h-4 w-4 text-ink transition-colors duration-300 group-hover:text-white" />
                  </span>
                </span>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>
    </>
  );
}
